import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, X, Zap, Send, RotateCcw, RefreshCw, FileClock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ExpandableTextarea } from '@/components/ExpandableTextarea';
import RichTextDisplay from '@/components/RichTextDisplay';
import { SearchableSelect } from '@/components/SearchableSelect';
import api from '@/lib/api';

const ACTION_HOUR_PHASES = ['New this Week', 'In Progress', 'Need Help / Blocked', 'Waiting on Client', 'Waiting for Filing', 'No Change This Week', 'Completed this Week'];
const phaseColors = {
  'New this Week': 'bg-blue-500/15 text-blue-400',
  'In Progress': 'bg-emerald-500/15 text-emerald-400',
  'Need Help / Blocked': 'bg-red-500/15 text-red-400',
  'Waiting on Client': 'bg-amber-500/15 text-amber-400',
  'Waiting for Filing': 'bg-violet-500/15 text-violet-400',
  'No Change This Week': 'bg-zinc-500/15 text-zinc-400',
  'Completed this Week': 'bg-teal-500/15 text-teal-400',
};

export function ActionHourTab({ users }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [editingActionItems, setEditingActionItems] = useState({});
  const [loading, setLoading] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);

  const fetchEntries = useCallback(() => {
    api.get('/action-hour').then(r => setEntries(r.data || [])).catch(() => {});
  }, []);

  const fetchProjects = useCallback(() => {
    api.get('/projects').then(r => setAllProjects(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchEntries(); fetchProjects(); }, [fetchEntries, fetchProjects]);

  // Re-fetch when browser tab becomes visible (handles board cleared by another user/session)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchEntries();
        fetchProjects();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchEntries, fetchProjects]);

  const addedProjectIds = new Set(entries.map(e => e.project_id));
  const availableProjects = allProjects.filter(p => !addedProjectIds.has(p.id) && p.status !== 'Archived');

  const handleAdd = async () => {
    if (!selectedProjectId) return toast.error('Select a project first');
    setLoading(true);
    try {
      await api.post('/action-hour', { project_id: selectedProjectId });
      toast.success('Project added to Action Hour');
      setSelectedProjectId('');
      fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error adding project'); }
    setLoading(false);
  };

  const handlePhaseChange = async (entryId, phase) => {
    await api.put(`/action-hour/${entryId}`, { phase });
    setEntries(prev => prev.map(e => e.id === entryId ? { ...e, phase } : e));
  };

  const handleSaveActionItems = async (entryId) => {
    const text = editingActionItems[entryId];
    if (text === undefined) return;
    try {
      await api.put(`/action-hour/${entryId}/action-items`, { action_items: text });
      toast.success('Action items updated');
      setEditingActionItems(prev => { const n = { ...prev }; delete n[entryId]; return n; });
      fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleRemove = async (entryId) => {
    await api.delete(`/action-hour/${entryId}`);
    setEntries(prev => prev.filter(e => e.id !== entryId));
    toast.success('Removed from board');
  };

  const handleClearAll = async () => {
    if (!window.confirm('Clear all projects from the Action Hour board? This is for weekly resets.')) return;
    await api.delete('/action-hour/clear');
    setEntries([]);
    toast.success('Board cleared for the week');
  };

  const handleSendDigest = async () => {
    if (!window.confirm('Send the Action Hour digest email to all staff?')) return;
    setSendingDigest(true);
    try {
      const r = await api.post('/action-hour/send-digest');
      toast.success(`Digest sent to ${r.data.recipient_count} team members`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error sending digest'); }
    setSendingDigest(false);
  };

  const handleRefresh = () => {
    fetchEntries();
    fetchProjects();
    toast.success('Board refreshed');
  };

  const grouped = {};
  for (const e of entries) {
    const assignee = e.latest_status?.assignee_name || e.added_by_name || 'Unassigned';
    if (!grouped[assignee]) grouped[assignee] = {};
    const phase = e.phase || 'New this Week';
    if (!grouped[assignee][phase]) grouped[assignee][phase] = [];
    grouped[assignee][phase].push(e);
  }

  return (
    <div className="space-y-4" data-testid="action-hour-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="w-72">
          <SearchableSelect
            value={selectedProjectId}
            onValueChange={setSelectedProjectId}
            options={availableProjects.map(p => ({ value: p.id, label: `${p.project_number} -- ${p.name}` }))}
            placeholder="Select a project to add..."
            searchPlaceholder="Search projects..."
            data-testid="action-hour-project-picker"
          />
        </div>
        <Button onClick={handleAdd} disabled={loading || !selectedProjectId} className="bg-primary text-primary-foreground" data-testid="action-hour-add-btn">
          <Plus className="h-4 w-4 mr-1" /> Add to Board
        </Button>
        <Button variant="ghost" size="icon" onClick={handleRefresh} className="h-9 w-9 text-muted-foreground hover:text-primary" title="Refresh board" data-testid="action-hour-refresh-btn">
          <RefreshCw className="h-4 w-4" />
        </Button>
        {entries.length > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleSendDigest} disabled={sendingDigest} className="text-xs border-border text-primary hover:text-primary/80" data-testid="action-hour-send-digest-btn">
              <Send className="h-3 w-3 mr-1" /> {sendingDigest ? 'Sending...' : 'Send Digest'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleClearAll} className="text-xs border-border text-muted-foreground hover:text-destructive" data-testid="action-hour-clear-btn">
              <RotateCcw className="h-3 w-3 mr-1" /> Clear Board
            </Button>
          </div>
        )}
      </div>

      {entries.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Zap className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No projects on the Action Hour board yet.</p>
          <p className="text-xs mt-1">Add projects above to populate the shared weekly view.</p>
        </div>
      ) : (
        Object.entries(grouped).map(([assignee, phases]) => (
          <div key={assignee} className="space-y-3">
            <div className="flex items-center gap-2 border-b border-border pb-1.5">
              <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold shrink-0">
                {assignee.charAt(0)}
              </div>
              <h3 className="text-sm font-semibold tracking-tight">{assignee}</h3>
            </div>
            {ACTION_HOUR_PHASES.filter(ph => phases[ph]?.length).map(phase => (
              <div key={phase} className="ml-4 space-y-2">
                <Badge className={`text-xs ${phaseColors[phase] || ''}`}>{phase}</Badge>
                {phases[phase].map(entry => (
                  <Card key={entry.id} className="border-border bg-background shadow-none" data-testid={`action-hour-entry-${entry.id}`}>
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => navigate(`/projects/${entry.project_id}?tab=status`)}
                          className="flex items-center gap-2 min-w-0 group hover:text-primary transition-colors text-left"
                          data-testid={`action-hour-open-status-${entry.id}`}
                          title="Open project Status Log"
                        >
                          <Badge variant="outline" className="text-xs font-mono shrink-0 group-hover:border-primary/40">{entry.project_number}</Badge>
                          <p className="text-sm font-medium truncate group-hover:underline">{entry.project_name}</p>
                          <span className="text-xs text-muted-foreground shrink-0">({entry.client_name})</span>
                        </button>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-primary hover:text-primary/80 gap-1 px-2"
                            onClick={() => navigate(`/projects/${entry.project_id}?tab=status`)}
                            data-testid={`action-hour-status-log-${entry.id}`}
                            title="Open Status Log"
                          >
                            <FileClock className="h-3 w-3" /> Status Log
                          </Button>
                          <Select value={entry.phase} onValueChange={v => handlePhaseChange(entry.id, v)}>
                            <SelectTrigger className="h-7 w-40 bg-transparent border-border text-xs" data-testid={`phase-select-${entry.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              {ACTION_HOUR_PHASES.map(p => <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => handleRemove(entry.id)} data-testid={`remove-entry-${entry.id}`}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      {entry.latest_status ? (
                        <div className="bg-secondary/30 rounded p-2 space-y-1.5">
                          <p className="text-xs text-muted-foreground">
                            Latest update ({new Date(entry.latest_status.created_at).toLocaleDateString()})
                            {entry.latest_status.assignee_name && <span className="ml-1">by <span className="text-foreground font-medium">{entry.latest_status.assignee_name}</span></span>}
                          </p>
                          <RichTextDisplay value={entry.latest_status.status_text} collapsedHeight={40} />
                          <div>
                            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Action Items</Label>
                            <ExpandableTextarea
                              className="bg-background border-border text-sm mt-0.5 min-h-[2rem]"
                              rows={2} expandedRows={6}
                              value={editingActionItems[entry.id] !== undefined ? editingActionItems[entry.id] : (entry.latest_status.action_items || '')}
                              onChange={e => setEditingActionItems(prev => ({ ...prev, [entry.id]: e.target.value }))}
                              data-testid={`action-items-input-${entry.id}`}
                            />
                            {editingActionItems[entry.id] !== undefined && editingActionItems[entry.id] !== (entry.latest_status.action_items || '') && (
                              <Button size="sm" onClick={() => handleSaveActionItems(entry.id)} className="mt-1.5 bg-primary text-primary-foreground text-xs h-7" data-testid={`save-action-items-${entry.id}`}>
                                Save
                              </Button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">No status updates yet for this project.</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}
